import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] text-md font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:border-destructive box-border h-10 leading-[1.5] active:scale-[0.98] hover:scale-[1.02] transform-gpu transition-colors transition-transform transition-shadow duration-200 ease-in-out transition-all duration-300 ease-in-out duration-300",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white shadow-xs hover:bg-primary/90 w-full text-md font-bold hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm transition-colors transition-transform transition-shadow duration-200 transition-all duration-300",
        destructive:
          "bg-transparent text-xs text-red-600 font-bold dark:bg-destructive/60 hover:bg-red-50 hover:text-red-700 active:bg-red-100  hover:scale-105 active:scale-95 hover:-translate-y-0.5 transition-colors transition-transform duration-150 ",
        outline:
          "border-[1px] border-neutral-400 text-md font-bold text-neutral-800 w-full bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50  hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm transition-colors transition-transform transition-shadow  transition-all duration-300",
        secondary:
          "bg-play text-white hover:bg-play/80 w-full text-md font-bold hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm transition-colors transition-transform transition-shadow duration-200",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 cursor-pointer hover:scale-105 active:scale-95 transition-colors transition-transform duration-150 transition-all",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80 transition-colors duration-200 transition-all",
        minimal:
          "bg-transparent text-xs text-muted font-bold hover:text-heading hover:bg-muted/10 hover:scale-105 active:scale-95 transition-colors transition-transform duration-150",
        icon: "bg-transparent text-white font-bold hover:text-neutral-900 hover:bg-neutral-100 hover:scale-105 active:scale-95 transition-colors transition-transform duration-150 transition-all",
        tertiary:
          "bg-neutral-100 text-neutral-600 font-bold hover:bg-neutral-100/80 hover:text-neutral-600/90 hover:scale-100 active:scale-95 w-full text-md font-bold hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm gap-1 transition-colors transition-transform transition-shadow duration-200 transition-all duration-300",
      },
      size: {
        default: "p-3 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  icon = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        icon &&
          "[&>svg]:h-8 [&>svg]:w-6 [&>svg[height]]:h-auto [&>svg[width]]:w-auto",
      )}
      {...props}
    />
  );
}

export { Button };
